import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from "@angular/core";
import {
    ControlContainer,
    FormGroup,
    FormGroupDirective,
    ReactiveFormsModule,
} from "@angular/forms";
import { TrafficDeviationService } from "@components/traffic-deviations/services/traffic-deviation.service";
import { AppStateService } from "@modules/core/services/app-state.service";
import { BehaviorSubject, combineLatest, filter, firstValueFrom, map, startWith, Subject, switchMap, takeUntil, withLatestFrom } from "rxjs";
import { FormSelectComponent } from "../form-select/form-select.component";
import { CommonModule } from "@angular/common";
import { FormSelectOption } from "../form-select/interfaces/form-select-option";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ValidationMessageComponent } from "../validation-message/validation-message.component";
import { TypedForm } from '@modules/shared/interfaces/typed-form/typed-form';
import { TrafficDeviationForm } from '@components/traffic-deviations/interfaces/traffic-deviation-form';
import moment from 'moment';
import { Router } from '@angular/router';
import { AffectedTrip } from './deviation-affected-trip';
import { TrafficDeviationTripService } from '@components/traffic-deviations/services/traffic-deviation-trip.service';
import { TrafficDeviationsStore } from '@components/traffic-deviations/stores/traffic-deviations.store';
import { TrafficDeviationTypeChangeOfJourneyTiming } from '@modules/shared/interfaces/traffic-deviations/traffic-deviation-type-change-of-journey-timing';
import { TrafficDeviationTypePredictionInaccurate } from '@modules/shared/interfaces/traffic-deviations/traffic-deviation-type-prediction-inaccurate';
import { TrafficDeviationTypeDeviationInformation } from '@modules/shared/interfaces/traffic-deviations/traffic-deviation-type-deviation-information';
import { TrafficDeviationTypeConnectionCancellation } from '@modules/shared/interfaces/traffic-deviations/traffic-deviation-type-connection-cancellation';
import { TrafficDeviationTypesUnion } from '@modules/shared/interfaces/traffic-deviations/traffic-deviation-union';
import { FormDateComponent } from '@modules/shared/components/form-date/form-date.component';
import { FormTimeComponent } from '@modules/shared/components/form-time/form-time.component';

@Component({
    selector: "app-deviation-affected-trip",
    templateUrl: "./deviation-affected-trip.component.html",
    styleUrls: ["./deviation-affected-trip.component.scss"],
    viewProviders: [
        { provide: ControlContainer, useExisting: FormGroupDirective },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormSelectComponent,
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        ValidationMessageComponent,
        FormDateComponent,
        FormTimeComponent
    ]
})
export class DeviationAffectedTripComponent implements OnInit, OnChanges, OnDestroy {
    @Input() id: string;
  
    private readonly _formGroupDirective = inject(FormGroupDirective);
    private readonly _appStateService = inject(AppStateService);
    private readonly _translateService = inject(TranslateService);
    private readonly _trafficDeviationsStore = inject(TrafficDeviationsStore);
    private readonly _deviationService = inject(TrafficDeviationService);
    private readonly _tripService = inject(TrafficDeviationTripService);
    private readonly _router = inject(Router);

    protected trafficDeviationAffectedGroup: FormGroup<TypedForm<TrafficDeviationForm['affected']>>;

    protected readonly selectedTrip$ = this._tripService.selectedTrip$.pipe(
      map(trip => trip && ({
        ...trip,
        description: this.getDescription(trip.deviationType, trip.startPlace, trip.endPlace),
      }))
    );
    protected readonly selectedConnection$ = this._tripService.selectedConnection$.pipe(
      map(trip => trip && ({
        ...trip,
        description: this.getDescription(trip.deviationType, trip.startPlace, trip.endPlace),
      }))
    );

    protected readonly selectedDeviationType$ = this._deviationService.selectedDeviationType$;
    protected readonly deviationType$ = this._deviationService.deviationType$;

    protected readonly transportAuthorityOptions = this._tripService.transportAuthorityOptions;
    protected readonly transportAuthorityOptions$ = this._tripService.transportAuthorityOptions$;
    protected readonly selectedTransportAuthorityOption$ = this.transportAuthorityOptions$.pipe(
      map(options => options?.find(o => o.selected)?.value)
    );

    protected readonly pointCallOptions = this._tripService.selectedPointCallOptions;
    protected readonly pointCallOptions$ = this._tripService.selectedPointCallOptions$;

    protected readonly connectionPointCallOptions = this._tripService.connectionPointCallOptions;
    protected readonly connectionPointCallOptions$: BehaviorSubject<ReadonlyArray<FormSelectOption>> = new BehaviorSubject([]);

    private _parentForm: FormGroup<TypedForm<TrafficDeviationForm>>;
    private readonly _destroy$ = new Subject<void>();

    ngOnInit() {
      this._parentForm = this._formGroupDirective.form;
      this.trafficDeviationAffectedGroup = this._parentForm.controls.affected;
      this._setupSubscriptions();
    }

    ngOnChanges(changes: SimpleChanges): void {
      if(changes.id) this._populateFormWithExistingTrafficDeviation();
    }

    private _setupSubscriptions(): void {
      combineLatest([this.selectedDeviationType$, this._tripService.vehicleAndactivityPointListItemObject$]).pipe(
        takeUntil(this._destroy$),
      ).subscribe(async ([selectedDeviationType, vehicleAndActivityPoint]) => {
        if (!vehicleAndActivityPoint?.vehicle) return; 
        if (!vehicleAndActivityPoint?.activityPointListItem) return;

        const vehicle = vehicleAndActivityPoint.vehicle;
        const hastusRegion = await firstValueFrom(this._appStateService.getSelectedGaragesState()
          .pipe(map((garages) => garages.find((garage) => garage.code === vehicle.garageCode)?.hastusRegionCode)));

        const activityPoints = vehicleAndActivityPoint.activityPointListItem.map((p) => p.activityPoint);
        if(!activityPoints.length) return;

        const firstCall = activityPoints.find(ap => ap.sequenceNumber === 1);
        const nextCall = activityPoints.find(ap => ap.stopId === vehicleAndActivityPoint.activityPointListItem.find(apli => apli.arrivesTo || apli.hasArrived)?.activityPoint.stopId);
        const lastCall = activityPoints.find(ap => ap.sequenceNumber === activityPoints.length);
        const deviation = 10; 

        this._tripService.setSelectedTrip({
          deviationType: selectedDeviationType,
          hastusRegion: hastusRegion,
          garageCode: vehicle.garageCode,
          inventoryNumber: vehicle.inventoryNumber,
          vehicleTaskNumber: vehicle.blockNumber,
          trafficDay: vehicle.trafficDate,
          routeName: vehicle.routeNumber,
          tripNumber: vehicle.tripNumber,
          startDate: moment().format('YYYY-MM-DD'),
          startTime: firstCall?.departureTime,
          endDate: moment().format('YYYY-MM-DD'),
          endTime: lastCall?.arrivalTime, 
          startPlaceId: firstCall?.stopId,
          startPlace: firstCall?.stopDescription,
          endPlaceId: lastCall?.stopId,
          endPlace: lastCall?.stopDescription,
          selectedPlaceId: nextCall?.stopId,
          selectedPlace: nextCall?.stopDescription,
          activityPointListItem: [...vehicleAndActivityPoint.activityPointListItem],
        });
        this._tripService.setPointCallOptions(activityPoints, nextCall);

        const affected = this.trafficDeviationAffectedGroup;

        affected.patchValue({
          startDate: moment().format('YYYY-MM-DD'),
          startTime: firstCall?.departureTime,
          endDate: moment().format('YYYY-MM-DD'),
          endTime: lastCall?.arrivalTime,
        });

        affected.controls.routeInfo.controls[0].patchValue({
          vehicleTaskNumber: vehicle.blockNumber,
          routeName: vehicle.routeNumber,
          routePublicName: vehicle.routeNumber,
          firstStopDestinationSign: lastCall.stopDescription,
          tripNumber: vehicle.tripNumber,
          trafficDay: vehicle.trafficDate,
          transportAuthorityNumber: '1',
        });
  
        switch (selectedDeviationType) {
          case 'ChangeOfJourneyTiming':
            affected.controls.routeInfo.controls[0].controls.calls.controls[0].patchValue({
              placeId: nextCall?.stopId,
              name: nextCall?.stopDescription,
            });
            if(affected.controls.routeInfo.controls[0].controls.calls.length < 2) affected.controls.routeInfo.controls[0].controls.calls.push(this._deviationService.addCall());
            affected.controls.routeInfo.controls[0].controls.calls.controls[1].patchValue({
              placeId: lastCall?.stopId,
              name: lastCall?.stopDescription,
            });
            affected.controls.minutes.patchValue(`${deviation}`);
            break;
          case 'ConnectionCancellation':
            affected.controls.routeInfo.controls[0].controls.calls.controls[0].patchValue({
              placeId: nextCall?.stopId,
              name: nextCall?.stopDescription,
            })
            affected.controls.routeInfo.push(this._deviationService.addRouteInfo({ trafficDay: vehicle.trafficDate }, [this._deviationService.addCall()] ));
            this.transportAuthorityOptions.setValue('1');
            break;
        }

        this._deviationService.resetRequiredValidators();
      });

      this._tripService.connectionVehicleAndactivityPointListItemObject$.pipe(
        withLatestFrom(this.selectedDeviationType$),
        takeUntil(this._destroy$),
      ).subscribe(async ([vehicleAndActivityPoint, selectedDeviationType]) => {
        if (!vehicleAndActivityPoint?.vehicle) return; 
        if (!vehicleAndActivityPoint?.activityPointListItem) return;

        const vehicle = vehicleAndActivityPoint.vehicle;
        const hastusRegion = await firstValueFrom(this._appStateService.getSelectedGaragesState()
          .pipe(map((garages) => garages.find((garage) => garage.code === vehicle.garageCode)?.hastusRegionCode)));

        const activityPoints = vehicleAndActivityPoint.activityPointListItem.map((p) => p.activityPoint);
        if(!activityPoints.length) return;

        const firstCall = activityPoints.find(ap => ap.sequenceNumber === 1);
        const nextCall = activityPoints.reduce((p, c) => moment().isBetween(moment(`${moment().format('YYYY-MM-DD')}T${p.departureTime}`), moment(`${moment().format('YYYY-MM-DD')}T${c.arrivalTime}`)) ? c : p);
        const lastCall = activityPoints.find(ap => ap.sequenceNumber === activityPoints.length);

        const affected = this.trafficDeviationAffectedGroup;
        if(selectedDeviationType === 'ConnectionCancellation') {
          this._tripService.setSelectedConnection({
            deviationType: selectedDeviationType,
            hastusRegion: hastusRegion,
            garageCode: vehicle.garageCode,
            inventoryNumber: vehicle.inventoryNumber,
            vehicleTaskNumber: vehicle.blockNumber,
            trafficDay: vehicle.trafficDate,
            routeName: vehicle.routeNumber,
            tripNumber: vehicle.tripNumber,
            startDate: moment().format('YYYY-MM-DD'),
            startTime: firstCall?.departureTime,
            endTime: lastCall?.arrivalTime, 
            endDate: moment().format('YYYY-MM-DD'),
            startPlaceId: firstCall?.stopId,
            startPlace: firstCall?.stopDescription,
            endPlaceId: lastCall?.stopId,
            endPlace: lastCall?.stopDescription,
            selectedPlaceId: nextCall?.stopId,
            selectedPlace: nextCall?.stopDescription,
            activityPointListItem: [...vehicleAndActivityPoint.activityPointListItem],
          });
          this._tripService.setConnectionPointCallOptions(activityPoints, nextCall);
          
          affected.controls.routeInfo.controls[1].patchValue({
            vehicleTaskNumber: vehicle.blockNumber,
            routeName: vehicle.routeNumber,
            routePublicName: vehicle.routeNumber,
            tripNumber: vehicle.tripNumber,
            trafficDay: vehicle.trafficDate,
            transportAuthorityNumber: '1',
          });
          affected.controls.routeInfo.controls[1].controls.calls.controls[0].patchValue({
            placeId: nextCall?.stopId,
            name: nextCall?.stopDescription,
          });
        }
      });

      combineLatest([
        this.trafficDeviationAffectedGroup.controls.minutes.valueChanges.pipe(
          startWith(this.trafficDeviationAffectedGroup.controls.minutes.value ?? '0')
        ),
        this.selectedTrip$,
        this.selectedDeviationType$
      ]).pipe(
        takeUntil(this._destroy$),
      ).subscribe(([minutes, trip, selectedDeviationType]) => {
        if (!trip?.trafficDay || !selectedDeviationType) return;

        if (selectedDeviationType === 'ChangeOfJourneyTiming') {
          const [endDate] = trip.endDate.split('T');
          let endDateTime = this._parseTrafficDayTimeFormat(endDate, trip.endTime);
          let addition = !this.id ? 10 : 0;
          endDateTime = endDateTime.add(minutes, 'm').add(addition, 'm');

          this.trafficDeviationAffectedGroup.controls.endDate.setValue(endDateTime.format('YYYY-MM-DD'));
          this.trafficDeviationAffectedGroup.controls.endTime.setValue(endDateTime.format('HH:mm'));
        }
      });

      this.pointCallOptions.valueChanges.pipe(
        withLatestFrom(this.pointCallOptions$, this.deviationType$),
        takeUntil(this._destroy$),
      ).subscribe(([selectedPointCallOption, pointCallOptions, deviationType]) => {
        const selected = pointCallOptions.find(c => c.value === selectedPointCallOption);
        if(!selected?.text) return;
        if(deviationType.deviationInformation || deviationType.predictionInaccurate) return;

        this.trafficDeviationAffectedGroup.controls.routeInfo.controls[0].controls.calls.controls[0].patchValue({
          placeId: selectedPointCallOption,
          name: selected.text,
        });
      });

      this.connectionPointCallOptions.valueChanges.pipe(
        withLatestFrom(this.connectionPointCallOptions$, this.deviationType$),
        takeUntil(this._destroy$),
      ).subscribe(([selectedConnectionPointCallOption, connectionPointCallOptions, deviationType]) => {
        if(!deviationType.connectionCancellation) return;
        if(!connectionPointCallOptions) return;

        const selectedCPCOptionName = connectionPointCallOptions.find(c => c.value === selectedConnectionPointCallOption);
        if(!selectedCPCOptionName?.text) return;

        this.trafficDeviationAffectedGroup.controls.routeInfo.controls[1].controls.calls.controls[0].patchValue({
          placeId: selectedConnectionPointCallOption,
          name: selectedCPCOptionName.text,
        });
      });

      combineLatest([
        this.selectedTransportAuthorityOption$,
        this.connectionPointCallOptions.valueChanges.pipe(startWith(this.connectionPointCallOptions.value ?? '')),
        this.deviationType$
      ]).pipe(
        takeUntil(this._destroy$),
      ).subscribe(([selectedTransportAuthority, selectedConnectionPointOption, deviationType]) => {
        if(!deviationType.connectionCancellation) return;
        
        if (selectedTransportAuthority === '1') {
          if (!selectedConnectionPointOption || selectedConnectionPointOption === '') {
            this.connectionPointCallOptions.setErrors({ noAffectedSelection: true });
          } else {
            this.connectionPointCallOptions.setErrors(null);
          }
        } else {
          this.connectionPointCallOptions.setErrors(null);
        }
      });
      
      this.transportAuthorityOptions.valueChanges.pipe(
        withLatestFrom(this.deviationType$),
        takeUntil(this._destroy$),
      ).subscribe(([selectedTransportAuthority, deviationType]) => {
        if(!deviationType.connectionCancellation) return;

        this.trafficDeviationAffectedGroup.controls.routeInfo.controls[1].patchValue({
          transportAuthorityNumber: selectedTransportAuthority,
        })
      });

      this.transportAuthorityOptions$.pipe(
        withLatestFrom(this.deviationType$),
        takeUntil(this._destroy$),
      ).subscribe(([options, deviationType]) => {
        if(!deviationType.connectionCancellation) return;
        if(!!this.transportAuthorityOptions.value) return;

        this.trafficDeviationAffectedGroup.controls.routeInfo.controls[1].patchValue({
          transportAuthorityNumber: `${options?.[0].value}`,
        })
      })

      this.transportAuthorityOptions$.pipe(
        switchMap(transportAuthorityOptions => {        
          const transportAuthority = transportAuthorityOptions.find(opts => opts.selected);
          if(transportAuthority?.value === "8") {
            return this._tripService.externalConnectionPointCallOptions$
          } else {
            return this._tripService.internalConnectionPointCallOptions$;
          }
        }),
        takeUntil(this._destroy$),
      ).subscribe(options => {
        if(!options) return;

        this.connectionPointCallOptions$.next(options);
        this.connectionPointCallOptions.setValue(`${options?.[0].value}`);
      });

      this.selectedTrip$.pipe(
        filter(selectedTrip => !!selectedTrip),
        takeUntil(this._destroy$),
      ).subscribe(({ selectedPlaceId }: AffectedTrip) => {
        this.pointCallOptions.setValue(selectedPlaceId);
      });

      this.selectedConnection$.pipe(
        filter(selectedConnection => !!selectedConnection),
        takeUntil(this._destroy$),
      ).subscribe(({ selectedPlaceId }: AffectedTrip) => {
        this.connectionPointCallOptions.setValue(selectedPlaceId);
      })
    }

    private _parseTrafficDayTimeFormat(trafficDate: string, timeString: string): moment.Moment {
      const [hoursStr, minutesStr] = timeString.split(':');
      const hours = Math.abs(parseInt(hoursStr, 10));
      const minutes = Math.abs(parseInt(minutesStr, 10));
      const date = moment(trafficDate);
      const daysToAdd = Math.floor(hours / 24);
      const normalizedHours = hours % 24;
  
      return date
          .add(daysToAdd, 'days')
          .add(normalizedHours, 'hours')
          .add(minutes, 'minutes');
    }

    private async _populateFormWithExistingTrafficDeviation(): Promise<void> {
      const trafficDeviation = await firstValueFrom(
        this._trafficDeviationsStore.getTrafficDeviationOnId(this.id)
      );
      if (!trafficDeviation) return;

      let affectedTrip: AffectedTrip = null;
      switch (trafficDeviation.type) {
        case 'ChangeOfJourneyTiming':
          const journeyTiming = trafficDeviation as TrafficDeviationTypeChangeOfJourneyTiming;
          const journeyTimingScope = journeyTiming.trafficDeviationScope;
          const journeyTimingRouteInfo = journeyTimingScope.routeInfo?.[0];
          const journeyTimingFromCall = journeyTimingRouteInfo.calls?.[0];
          const journeyTimingToCall = journeyTimingRouteInfo.calls?.[1];
          const journeyTimingStartDate = moment(journeyTimingScope.startDate);
          const journeyTimingEndDate = moment(journeyTimingScope.endDate);

          affectedTrip = {
            deviationType: journeyTiming.type,
            hastusRegion: journeyTiming.hastusRegion,
            garageCode: journeyTiming.garageCode,
            vehicleTaskNumber: journeyTimingRouteInfo.vehicleTaskNumber,
            trafficDay: journeyTimingRouteInfo.trafficDay,
            routeName: journeyTimingRouteInfo.routeName,
            tripNumber: journeyTimingRouteInfo.tripNumber,
            startDate: journeyTimingScope.startDate,
            startTime: journeyTimingStartDate.format('HH:mm'),
            endDate: journeyTimingScope.endDate,
            endTime: journeyTimingEndDate.subtract(journeyTimingScope.minutes, 'm').format('HH:mm'), 
            startPlaceId: journeyTimingFromCall.placeId,
            startPlace: journeyTimingFromCall.name || journeyTiming.trafficDeviationScope.affectedTrip?.startPlace,
            endPlaceId: journeyTimingToCall.placeId,
            endPlace: journeyTimingToCall.name || journeyTiming.trafficDeviationScope.affectedTrip?.endPlace,
            selectedPlaceId: journeyTimingFromCall.placeId,
            selectedPlace: journeyTimingFromCall.name
          };

          if (journeyTimingScope.affectedTrip) {
            affectedTrip = journeyTimingScope.affectedTrip;
          }
          break;
        case 'PredictionInaccurate':
          const predictionInaccurate = trafficDeviation as TrafficDeviationTypePredictionInaccurate;
          const predictionInaccurateScope = predictionInaccurate.trafficDeviationScope;
          const predictionInaccurateRouteInfo = predictionInaccurateScope.routeInfo?.[0];
          const predictionInaccurateStartDate = moment(predictionInaccurateScope.startDate);
          const predictionInaccurateEndDate = moment(predictionInaccurateScope.endDate);

          affectedTrip = {
            deviationType: predictionInaccurate.type,
            hastusRegion: predictionInaccurate.hastusRegion,
            garageCode: predictionInaccurate.garageCode,
            vehicleTaskNumber: predictionInaccurateRouteInfo.vehicleTaskNumber,
            trafficDay: predictionInaccurateRouteInfo.trafficDay,
            routeName: predictionInaccurateRouteInfo.routeName,
            tripNumber: predictionInaccurateRouteInfo.tripNumber,
            startDate: predictionInaccurateScope.startDate,
            startTime: predictionInaccurateStartDate.format('HH:mm'),
            endDate: predictionInaccurateScope.endDate,
            endTime: predictionInaccurateEndDate.format('HH:mm'), 
            startPlace: predictionInaccurate.trafficDeviationScope.affectedTrip?.startPlace,
            endPlace: predictionInaccurate.trafficDeviationScope.affectedTrip?.endPlace
          };

          if (predictionInaccurateScope.affectedTrip) {
            affectedTrip = predictionInaccurateScope.affectedTrip;
          }
          break;
        case 'ConnectionCancellation':
          const connectionCancellation = trafficDeviation as TrafficDeviationTypeConnectionCancellation;
          const connectionCancellationScope = connectionCancellation.trafficDeviationScope;
          const departingInfo = connectionCancellationScope.routeInfo?.[0];
          const arrivingInfo = connectionCancellationScope.routeInfo?.[1];
          const departingCall = departingInfo.calls?.[0];
          const arrivingCall = arrivingInfo.calls?.[0]; 
          const connectionCancellationStartDate = moment(connectionCancellationScope.startDate);
          const connectionCancellationEndDate = moment(connectionCancellationScope.endDate);

          affectedTrip = {
            deviationType: connectionCancellation.type,
            hastusRegion: connectionCancellation.hastusRegion,
            garageCode: connectionCancellation.garageCode,
            vehicleTaskNumber: departingInfo.vehicleTaskNumber,
            trafficDay: departingInfo.trafficDay,
            routeName: departingInfo.routeName,
            tripNumber: departingInfo.tripNumber,
            startDate: connectionCancellationScope.startDate,
            startTime: connectionCancellationStartDate.format('HH:mm'),
            endDate: connectionCancellationScope.endDate,
            endTime: connectionCancellationEndDate.format('HH:mm'), 
            startPlaceId: departingCall.placeId,
            startPlace: departingCall.name || connectionCancellation.trafficDeviationScope.affectedTrip?.startPlace,
            endPlace: connectionCancellation.trafficDeviationScope.affectedTrip?.endPlace
          };

          if (connectionCancellationScope.affectedTrip) {
            affectedTrip = connectionCancellationScope.affectedTrip;
          }

          this.transportAuthorityOptions.setValue(arrivingInfo.transportAuthorityNumber ?? '1');
          this._tripService.setSelectedConnection({
            deviationType: connectionCancellation.type,
            hastusRegion: connectionCancellation.hastusRegion,
            garageCode: connectionCancellation.garageCode,
            vehicleTaskNumber: departingInfo.vehicleTaskNumber,
            trafficDay: arrivingInfo.trafficDay,
            routeName: arrivingInfo.routeName,
            tripNumber: arrivingInfo.tripNumber,
            startDate: connectionCancellationScope.startDate,
            startTime: connectionCancellationStartDate.format('HH:mm'),
            endDate: connectionCancellationScope.endDate,
            endTime: connectionCancellationEndDate.format('HH:mm'), 
            startPlaceId: arrivingCall.placeId,
            startPlace: arrivingCall.name,
          })
          break;
        case 'DeviationInformation':
          const deviationInformation = trafficDeviation as TrafficDeviationTypeDeviationInformation
          const deviationInformationScope = deviationInformation.trafficDeviationScope;
          const deviationInformationRouteInfo = deviationInformationScope.routeInfo?.[0];
          const deviationInformationStartDate = moment(deviationInformationScope.startDate);
          const deviationInformationEndDate = moment(deviationInformationScope.endDate);

          affectedTrip = {
            deviationType: deviationInformation.type,
            hastusRegion: deviationInformation.hastusRegion,
            garageCode: deviationInformation.garageCode,
            vehicleTaskNumber: deviationInformationRouteInfo.vehicleTaskNumber,
            trafficDay: deviationInformationRouteInfo.trafficDay,
            routeName: deviationInformationRouteInfo.routeName,
            tripNumber: deviationInformationRouteInfo.tripNumber,
            startDate: deviationInformationScope.startDate,
            startTime: deviationInformationStartDate.format('HH:mm'),
            endDate: deviationInformationScope.endDate,
            endTime: deviationInformationEndDate.format('HH:mm'), 
            startPlace: deviationInformation.trafficDeviationScope.affectedTrip?.startPlace,
            endPlace: deviationInformation.trafficDeviationScope.affectedTrip?.endPlace
          };

          if (deviationInformationScope.affectedTrip) {
            affectedTrip = deviationInformationScope.affectedTrip;
          }
          break;
      }

      this._tripService.setSelectedTrip(affectedTrip);
    }

    protected getDescription(deviationType: TrafficDeviationTypesUnion['type'], startPlace?: string, endPlace?: string): string {
      if (!startPlace && !endPlace) return this._translateService.instant(deviationType);
      if (!endPlace) return startPlace;
      if (startPlace === endPlace) return startPlace;

      return `${startPlace} - ${endPlace}`;
    }

    protected showPicker(currentTarget: EventTarget | HTMLInputElement): void {
      const input = (<HTMLInputElement>currentTarget);
      if(input.readOnly) return;
      
      input.showPicker();
    }
  
    protected onKeydownPicker(event: KeyboardEvent, elementRef: HTMLInputElement): void {
      if (event.code === "Enter" || event.code === "Space") {
        this.showPicker(elementRef);
      }
    } 
  
    protected isDateFieldsSet(): boolean {
      const startDate = this.trafficDeviationAffectedGroup.controls.startDate.value;
      const endDate = this.trafficDeviationAffectedGroup.controls.endDate.value;
      const startTime = this.trafficDeviationAffectedGroup.controls.startTime.value;
      const endTime = this.trafficDeviationAffectedGroup.controls.endTime.value;
      if (startDate && endDate && startTime && endTime) {
        return true;
      }
  
      return false;
    }

    selectStop(scope: 'DEFAULT'|'CONNECTION'): void {
      this._router.navigate(['', { outlets: { outlet1: ['selected-call', scope] }}]);
    }

    selectVehicle(): void {
      this._tripService.setConnectionCancellationFlow();
      this._router.navigate(['', { outlets: { outlet1: ['selected-vehicle'] } }]);
    }

    add(): void {
      this.trafficDeviationAffectedGroup.controls.minutes.setValue(`${parseInt(this.trafficDeviationAffectedGroup.controls.minutes.value) + 1}`);
    }

    subtract(): void {
      if(this.trafficDeviationAffectedGroup.controls.minutes.value === '0') return;

      this.trafficDeviationAffectedGroup.controls.minutes.setValue(`${parseInt(this.trafficDeviationAffectedGroup.controls.minutes.value) - 1}`);
    }

    ngOnDestroy(): void {
      this._destroy$.next();
      this._destroy$.complete();
      this.trafficDeviationAffectedGroup.reset();
      this.transportAuthorityOptions.reset();
      this._tripService.resetTripState();
    }
}
